import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { Controller, useForm, type UseFormReturn } from "react-hook-form";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import {
  getFlightSearchFormSchema,
  type FlightSearchFormInputValues,
  type FlightSearchFormValues,
} from "@/features/flights/utils/flightsValidation";
import { usePreviousFlightSearchData } from "@/features/search/hooks/usePreviousSearchData";
import { useSnackbarRHFErrors } from "@/hooks/useSnackbarRHFErrors";
import {
  FlightSearchTrackingEvents,
  rudderAnalytics,
  type SearchSource,
} from "@/lib/rudderstack";

import { TripTypeRadioGroup } from "./components/TripTypeRadioGroup";
import { FlightDatePicker } from "./sections/FlightDatePicker";
import { FlightLocationPicker } from "./sections/FlightLocationPicker";
import { FlightTravelersPicker } from "./sections/FlightTravelersPicker";

export type FlightSearchForm = UseFormReturn<
  FlightSearchFormInputValues,
  undefined,
  FlightSearchFormValues
>;

type FlightSearchFormProps = {
  submitButtonContent: React.ReactNode;
  initialValues?: FlightSearchFormInputValues | null;
  showErrorsInline?: boolean;
  onSubmit: (values: FlightSearchFormValues) => void;
  analyticsSource: SearchSource;
};

export function FlightSearchForm({
  onSubmit,
  initialValues,
  analyticsSource,
  showErrorsInline,
  submitButtonContent,
}: FlightSearchFormProps) {
  const intl = useIntl();

  const { previousSearchData } = usePreviousFlightSearchData();

  const schema = useMemo(() => getFlightSearchFormSchema(intl), [intl]);

  const form = useForm<
    FlightSearchFormInputValues,
    undefined,
    FlightSearchFormValues
  >({
    defaultValues: initialValues || previousSearchData,
    resolver: zodResolver(schema),
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  useSnackbarRHFErrors<FlightSearchFormInputValues>({
    disabled: showErrorsInline,
    errors: form.formState.errors,
    touched: form.formState.touchedFields,
    submitCount: form.formState.submitCount,
  });

  return (
    <form
      onSubmit={form.handleSubmit((values) => onSubmit(values))}
      noValidate
      acceptCharset="ISO-8859-1"
      aria-label={intl.formatMessage({ id: "flights.search.flightSearchForm" })}
      className="flex flex-col gap-2"
    >
      <Controller
        control={form.control}
        name="tripType"
        render={({ field }) => (
          <TripTypeRadioGroup
            value={field.value}
            onChange={(event, value) => {
              rudderAnalytics.track(
                FlightSearchTrackingEvents.USER_CHANGED_TRIP_TYPE,
                {
                  source: analyticsSource,
                  type: value,
                },
              );

              field.onChange(event);
            }}
          />
        )}
      />

      <div className="flex flex-col gap-2">
        <FlightLocationPicker
          analyticsSource={analyticsSource}
          form={form}
          showErrorsInline={showErrorsInline}
        />

        <FlightDatePicker form={form} analyticsSource={analyticsSource} />

        <FlightTravelersPicker form={form} analyticsSource={analyticsSource} />
      </div>

      <div className="mt-2">
        <Button fullWidth variant="contained" type="submit">
          {submitButtonContent}
        </Button>
      </div>
    </form>
  );
}
